/**
 * Knowledge Center Client API service (Kcc) — Genie คุยกับ App Knowledge Center ผ่าน **proxy ฝั่ง server ของ Genie**
 *
 * ห่อ endpoint ใต้ `api/v1/KnowledgeCenterClient` (KnowledgeCenterClientController) แบบ promise ที่ **ไม่เด้ง popup
 * และไม่เด้ง logout** — ห้ามเปลี่ยนไปใช้ `this.$call.*` เด็ดขาด: `$call` เด้ง Swal ทุก non-200 และเจอ 401 จะล้าง
 * session ไป /login ⇒ Center ปฏิเสธ key ครั้งเดียวจะดีดผู้ใช้ออกจากระบบ (proxy จึงแปลง 401/403 ของ Center เป็น 424)
 * หน้าจอ (การ์ด Core Config, โหมด Center ในหน้า MetaDoc, picker ใน Flow builder) แสดง error เองแบบ inline
 *
 * ── key ไม่ถึง browser
 * Admin key / Runtime key อยู่ที่ server เท่านั้น (Core Configurations, `sy.Dbsettings`) · `settings()` คืนแค่ธง
 * `hasAdminKey/hasRuntimeKey` · `saveSettings()` ส่ง key ช่องว่าง/null = คงค่าเดิม, `clearAdminKey/clearRuntimeKey`
 * = ล้าง · ไฟล์นี้ไม่ตั้ง header ของ Center เอง — proxy เป็นคนประกอบ header ต่อคำขอ
 *
 * ── HTTP status ที่ proxy ตอบ (TASK.md A13) — ทุก error พก `data = { reason, correlationId, centerHttpStatus }`
 *   200            สำเร็จ (ResponseModel.status = 0) → เมธอดคืน `data`
 *   400            ข้อมูลไม่ผ่าน (Center หรือ Genie validate — เช่น BaseUrl/Channel/timeout ตอน saveSettings)
 *   401            **Genie** ไม่ให้สิทธิ์ (role ไม่อยู่ใน APIAuthen ของ path นั้น — เช่น role 2 เรียก settings) ไม่ใช่ Center
 *   404            ไม่พบเอกสาร (ResponseModel.status = 6)
 *   409            ชื่อเอกสาร/ItemId ซ้ำ
 *   412            ยังไม่ได้ตั้งค่า — ปิดโหมด Center / ไม่มี Base URL / ไม่มี key ที่ endpoint นั้นใช้
 *   424            Center ปฏิเสธ key (Center ตอบ 401/403) · `data.reason` = MissingHeader|Malformed|NotFound|Revoked|
 *                  Inactive|Expired|Error|WrongKeyType — **ไม่ใช่ 401** โดยตั้งใจ ⇒ ไม่มีทางเด้ง logout
 *   502            ติดต่อ Center ไม่ได้ — timeout / unreachable / Center 5xx / body อ่านไม่ได้
 * `kccErrorKey(err)` ข้างล่างแปลง status เหล่านี้เป็นชื่อ i18n `kcc*` ให้ทุกหน้าใช้ข้อความชุดเดียวกัน
 *
 * ── error ที่ throw
 * `Error` มี `message` (จาก ResponseModel ถ้ามี), `httpStatus` (HTTP code จริง), `status` (APIStatus) และ `data`
 *
 * ── รูปข้อมูล (STJ camelCase — TASK.md Contract §3–4)
 *   status()          → `{ enabled, runtimeReady, adminReady, baseUrlDisplay, channel }` (ไม่ยิง Center)
 *   catalog()         → `[{ vectordbName, nameTh, nameEn, title, ownerCompanyId }]` (Runtime key)
 *   documents()       → `{ own: [KccDocument], shared: [KccDocument] }`
 *   KccDocument       = `{ vectordbName, itemId, nameTh, nameEn, nameCn, title, subTitle, activeStatus, ownerCompanyId,
 *                         ownerCompanyName, scope: 'owner'|'edit'|'view', policy, shares, updateDate }`
 *   setPolicy()       → `{ vectordbName, minLevelCode, allowedChannels: [], fromDatabase }`
 *   setShare()/removeShare() → `[{ vectordbName, targetCompanyId, targetCompanyName, permission, updateDate, updateBy }]`
 *   companies()       → `[{ companyId, companyName }]`
 *   channels()        → `[{ id, code, nameTh, nameEn, ceilingLevelCode, isActive, sortOrder }]`
 *   settings()/saveSettings() → `{ enabled, baseUrl, hasAdminKey, hasRuntimeKey, channel, timeoutSeconds,
 *                                  uploadTimeoutSeconds, warning, updateDate, updateBy }`
 *   testConnections() → `{ admin: KccProbeResult, runtime: KccProbeResult }` (200 เสมอ — ผลอยู่ใน data)
 *   KccProbeResult    = `{ ok, skipped, httpStatus, reason, message, count, correlationId, durationMs }`
 *
 * โครง axios / base-URL / cookie-auth / unwrap / toError ลอกจาก `services/accessLevelApi.js` (error ที่ `unwrap()`
 * สร้างเองติดธง `isUnwrapped` — axios 1.x ตั้ง `error.status` ให้ AxiosError ด้วย ห้ามใช้เป็นตัวแยก)
 * `import.meta.env?.` (มี `?.`) เพื่อให้ `tests/kccApi.test.mjs` import ไฟล์นี้ใต้ Node เปล่าได้ — Vite แทนค่าตอน build ตามเดิม
 */

import axios from 'axios';

const BASE_URL = import.meta.env?.VITE_APP_API || 'http://localhost:5000/';

/** axios instance ของ Kcc เอง (interceptor บน axios ตัว default ไม่มาเกี่ยว) · export ไว้ให้เทสต์ใส่ adapter ปลอม */
export const kccHttp = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/** แกะ ResponseModel `{ message, status, data }` → `data` · throw เมื่อ envelope บอกว่าไม่สำเร็จ */
export function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && ('status' in body || 'Status' in body)) {
    const status = 'status' in body ? body.status : body.Status;
    const data = 'data' in body ? body.data : body.Data;
    if (status === API_STATUS_SUCCESS) {
      return data;
    }
    const err = new Error(body.message || body.Message || 'Request failed');
    err.status = status;
    err.httpStatus = response.status;
    err.data = data;
    err.isUnwrapped = true;
    throw err;
  }
  return body;
}

/** แปลง error ของ axios ให้เป็น Error ที่พก message + HTTP status + data ของ API มาด้วย */
export function toError(error) {
  // error ที่ unwrap() สร้างเองไม่มี `response` — ห่อซ้ำแล้ว httpStatus/data จะหาย
  if (error && error.isUnwrapped) return error;

  const body = error && error.response && error.response.data;
  const bodyIsObject = body && typeof body === 'object';
  const err = new Error(
    (bodyIsObject && (body.message || body.Message)) || (error && error.message) || 'Request failed'
  );
  err.httpStatus = error && error.response && error.response.status;
  err.status = bodyIsObject ? body.status ?? body.Status : undefined;
  err.data = bodyIsObject ? body.data ?? body.Data : undefined;
  return err;
}

/**
 * ชื่อ i18n ของข้อความ error ระดับ "การเชื่อมต่อ Center" — ใช้ร่วมกันทุกหน้า (ต่อท้ายด้วย `err.data?.reason` เอง)
 * 424 ⇒ `kccKeyRejected` · 502/504 ⇒ `kccCenterUnreachable` · 412 ⇒ `kccNotConfigured` · อื่น ๆ ⇒ `kccCenterError`
 * (400/404/409 มีข้อความเฉพาะใน `err.message` จาก server — หน้าจอเลือกเองว่าจะโชว์ message หรือข้อความกลาง)
 * @param {*} err error ที่เมธอดของไฟล์นี้ throw
 * @returns {string}
 */
export function kccErrorKey(err) {
  const httpStatus = err && err.httpStatus;
  if (httpStatus === 424) return 'kccKeyRejected';
  if (httpStatus === 502 || httpStatus === 504) return 'kccCenterUnreachable';
  if (httpStatus === 412) return 'kccNotConfigured';
  return 'kccCenterError';
}

async function request(send) {
  try {
    return unwrap(await send());
  } catch (e) {
    throw toError(e);
  }
}

/**
 * ตัด query param ที่ไม่มีค่า (undefined / null / สตริงว่าง) ทิ้ง — **เก็บ 0 ไว้** (`targetCompanyId=0` เป็น company
 * จริงบน dev) · ลอกจาก service ฝั่ง Center
 */
function cleanParams(params) {
  const out = {};
  if (!params || typeof params !== 'object') return out;
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null) return;
    if (typeof value === 'string' && value.trim() === '') return;
    out[key] = value;
  });
  return out;
}

const get = (url, params) => request(() => kccHttp.get(url, { params: cleanParams(params) }));
const post = (url, payload) => request(() => kccHttp.post(url, payload || {}));
const put = (url, payload) => request(() => kccHttp.put(url, payload || {}));
const del = (url, params) => request(() => kccHttp.delete(url, { params: cleanParams(params) }));

const ROOT = 'api/v1/KnowledgeCenterClient';

export const kccApi = {
  // ── สถานะ / catalog (role 0,1,2,6) ─────────────────────────────────────────

  /**
   * โหมด Center เปิดไหม + พร้อมแค่ไหน — ไม่ยิง Center (อ่านค่าตั้งที่ server) · ใช้ตัดสินโหมดใน MetaDoc/Flow builder
   * @returns {Promise<{enabled: boolean, runtimeReady: boolean, adminReady: boolean, baseUrlDisplay: ?string, channel: string}>}
   */
  status() {
    return get(`${ROOT}/status`);
  },

  /**
   * รายชื่อเอกสารที่ Runtime key ค้นได้ (`runtime/documents` ของ Center) — picker ของ `documents` plugin ใน Flow builder
   * · โหมดปิด/ไม่มี Runtime key ⇒ 412
   * @returns {Promise<Array<{vectordbName: string, nameTh: ?string, nameEn: ?string, title: ?string, ownerCompanyId: ?number}>>}
   */
  catalog() {
    return get(`${ROOT}/catalog`);
  },

  /**
   * graph ของ Meta Community ฝั่ง Center (`runtime/graphs` — id ของ Center) — dropdown MCG ของ Flow builder โหมด Center
   * · โหมดปิด/ไม่มี Runtime key ⇒ 412 · Center ปฏิเสธ key ⇒ 424 · Center รุ่นเก่า/ล่ม ⇒ 502
   * @returns {Promise<Array<{id: number, communityName: string, description: ?string, groupCount: ?number, documentCount: number, isActive: boolean}>>}
   */
  graphs() {
    return get(`${ROOT}/graphs`);
  },

  // ── เอกสาร (Admin key · role 0,1,2,6) ─────────────────────────────────────

  /**
   * เอกสาร "ของเรา" + "ได้รับแชร์" · `search` ว่าง ⇒ ทั้งหมด
   * @param {string} [search]
   * @returns {Promise<{own: Array, shared: Array}>}
   */
  documents(search) {
    return get(`${ROOT}/documents`, { search });
  },

  /**
   * รายละเอียดเอกสาร (รวม policy + shares) · ไม่พบ ⇒ 404
   * @param {string} name vectordbName
   */
  documentDetail(name) {
    return get(`${ROOT}/documents/detail`, { name });
  },

  /**
   * สร้างเอกสาร (header เท่านั้น — เนื้อหาตามมาทาง `uploadVersion`) · ชื่อ/ItemId ซ้ำ ⇒ 409
   * @param {{title: string, subTitle?: string, nameTh?: string, nameEn?: string, nameCn?: string, keyword?: string, itemId?: string, vectordbName: string}} body
   */
  createDocument(body) {
    return post(`${ROOT}/documents/create`, body);
  },

  /**
   * แก้ metadata (vectordbName/itemId แก้ไม่ได้) — owner/edit
   * @param {{vectordbName: string, title?: string, subTitle?: string, nameTh?: string, nameEn?: string, nameCn?: string, keyword?: string}} body
   */
  updateMetadata(body) {
    return put(`${ROOT}/documents/metadata`, body);
  },

  /**
   * อัปโหลดไฟล์เป็น version ใหม่ (≤ 500 MB) → `{ versionHeadId }` — Center แปลงเอกสารเสร็จก่อนจึงตอบ ⇒ อาจนานหลายนาที
   * (ไม่ตั้ง timeout ฝั่ง browser — server คุมด้วย UploadTimeoutSeconds)
   *
   * ⚠️ instance นี้ตั้ง `Content-Type: application/json` เป็นค่าตั้งต้น — ถ้าปล่อยไว้ axios 1.x จะแปลง `FormData` เป็น
   *    JSON (`transformRequest`: FormData + JSON content type ⇒ `JSON.stringify(formDataToJSON(data))`) ไฟล์หายเงียบ
   *    ⇒ ทับด้วย `multipart/form-data` (ท่าเดียวกับ `api/call.js`) แล้ว adapter ของ browser จะลบทิ้งให้ browser
   *    ใส่ boundary เอง · **ห้าม**ประกอบ boundary/Content-Type เองที่ผู้เรียก
   * ช่องใน FormData (ชื่อตาม Contract §4 `KccVersionUploadForm`): `vectordbName, file, fileName?, effectiveStartDate?,
   * effectiveEndDate?, note?, activeStatus?`
   * @param {FormData} formData
   * @param {(e: import('axios').AxiosProgressEvent) => void} [onUploadProgress]
   * @returns {Promise<{versionHeadId: number}>}
   */
  uploadVersion(formData, onUploadProgress) {
    return request(() =>
      kccHttp.post(`${ROOT}/documents/version`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress,
      })
    );
  },

  /**
   * เปิด/ปิดเอกสาร — owner
   * @param {{vectordbName: string, activeStatus: boolean}} body
   */
  setActive(body) {
    return post(`${ROOT}/documents/active`, body);
  },

  /**
   * ลบเอกสาร — owner · path แยกเป็น `/documents/delete` เพราะ APIAuthen จับคู่สิทธิ์ด้วย path ไม่ดู method
   * @param {string} name vectordbName
   * @returns {Promise<{deleted: boolean}>}
   */
  deleteDocument(name) {
    return del(`${ROOT}/documents/delete`, { name });
  },

  // ── Policy / แชร์ / รายชื่ออ้างอิง (Admin key · role 0,1) ────────────────

  /**
   * ตั้งขั้นต่ำ T + channel ที่อนุญาต (`allowedChannels: []` = ทุก channel) — owner
   * @param {{vectordbName: string, minLevelCode: string, allowedChannels: string[]}} body
   */
  setPolicy(body) {
    return put(`${ROOT}/documents/policy`, body);
  },

  /**
   * แชร์ให้ company อื่น หรือสลับสิทธิ์ View↔Edit — owner → รายการแชร์ทั้งหมดหลังบันทึก
   * @param {{vectordbName: string, targetCompanyId: number, permission: 'View'|'Edit'}} body
   */
  setShare(body) {
    return put(`${ROOT}/documents/share`, body);
  },

  /**
   * ยกเลิกการแชร์ — owner → รายการแชร์ที่เหลือ
   * @param {string} name vectordbName
   * @param {number} targetCompanyId
   */
  removeShare(name, targetCompanyId) {
    return del(`${ROOT}/documents/share/remove`, { name, targetCompanyId });
  },

  /** รายชื่อ company ที่แชร์ให้ได้ → `[{ companyId, companyName }]` */
  companies() {
    return get(`${ROOT}/companies`);
  },

  /** Channel ของ Center (ใช้ใน dialog Policy) → `[{ id, code, nameTh, nameEn, ceilingLevelCode, isActive, sortOrder }]` */
  channels() {
    return get(`${ROOT}/channels`);
  },

  // ── ค่าตั้ง (Core Configurations · role 0,1) ───────────────────────────────

  /**
   * ค่าตั้งปัจจุบัน — **ไม่มีค่า key** มีแค่ `hasAdminKey/hasRuntimeKey` · role 2 ⇒ 401 (การ์ดซ่อนตัวเอง)
   */
  settings() {
    return get(`${ROOT}/settings`);
  },

  /**
   * บันทึกค่าตั้ง → view หลังบันทึก · key ว่าง/null = คงค่าเดิม · `clearAdminKey/clearRuntimeKey: true` = ล้าง ·
   * BaseUrl/Channel/timeout ไม่ผ่าน ⇒ 400
   * @param {{enabled: boolean, baseUrl: string, adminKey?: ?string, runtimeKey?: ?string, clearAdminKey?: boolean,
   *          clearRuntimeKey?: boolean, channel?: string, timeoutSeconds?: ?number, uploadTimeoutSeconds?: ?number}} body
   */
  saveSettings(body) {
    return post(`${ROOT}/settings/save`, body);
  },

  /**
   * ทดสอบการเชื่อมต่อแยกใบ (Admin → `manage/channels`, Runtime → `runtime/documents` ของ Center) ด้วยค่าในฟอร์ม
   * ณ ตอนกด · ช่องว่าง = ใช้ค่าที่บันทึกไว้ · ไม่มีค่าใดเลย ⇒ ใบนั้น `skipped` · ตอบ 200 เสมอ ผลอยู่ใน `admin/runtime`
   * @param {{baseUrl?: ?string, adminKey?: ?string, runtimeKey?: ?string}} body
   */
  testConnections(body) {
    return post(`${ROOT}/settings/test`, body);
  },
};

export default kccApi;
